// src/templates/mobile/CardListMobile.jsx
import React, { useState, useRef } from "react";
import { 
  IconEdit, 
  IconTrash, 
  IconSearch, 
  IconLoading, 
  IconClose,
  IconChevronDown 
} from "../../components/Icons/Icons";

const STATUS_CONFIG = {
  ativo: { label: "Ativo", cor: "#22c55e" },
  inativo: { label: "Inativo", cor: "#94a3b8" },
  pendente: { label: "Pendente", cor: "#f59e0b" },
  aberta: { label: "Aberta", cor: "#3b82f6" },
  em_andamento: { label: "Em Andamento", cor: "#8b5cf6" },
  finalizada: { label: "Finalizada", cor: "#22c55e" },
  cancelada: { label: "Cancelada", cor: "#ef4444" },
  alta: { label: "Alta", cor: "#ef4444" },
  media: { label: "Média", cor: "#f59e0b" },
  baixa: { label: "Baixa", cor: "#22c55e" },
};

function CardListMobile({ 
  items = [],
  carregando = false,
  erro = "",
  titulo = "Itens",
  searchPlaceholder = "Buscar...",
  onNovo,
  onEditar,
  onExcluir,
  onHome,
  onAcaoPrimaria,
  acaoPrimariaLabel = "Ação",
  getAcaoPrimariaLabel,
  acaoPrimariaIcon,
  novoLabel = "Novo",
  renderCard,
  getSearchText,
  emptyMessage = "Nenhum item encontrado",
  carregandoMessage = "Carregando...",
  hideDefaultActions = false,
  customActions = null,
  showColorBar = true
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCards, setExpandedCards] = useState({});
  const [swipedCard, setSwipedCard] = useState(null);
  const touchStart = useRef(null);
  const touchEnd = useRef(null);
  
  const filteredItems = items.filter(item => {
    if (!searchTerm.trim()) return true;
    const searchText = getSearchText ? getSearchText(item) : (item.nome || "");
    return searchText.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getInitials = (text) => {
    if (!text) return "?";
    return text.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
  };

  const toggleExpand = (id) => {
    setExpandedCards(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleTouchStart = (e, id) => {
    touchStart.current = e.touches[0].clientX;
    touchEnd.current = null;
  };

  const handleTouchMove = (e) => {
    touchEnd.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (id) => {
    if (!touchStart.current || !touchEnd.current) return;
    const distance = touchStart.current - touchEnd.current;
    const isSwipeLeft = distance > 80;
    const isSwipeRight = distance < -80;
    
    if (isSwipeLeft) {
      setSwipedCard(id);
    } else if (isSwipeRight) {
      setSwipedCard(null);
    }
    
    touchStart.current = null;
    touchEnd.current = null;
  };

  const renderStatusChip = (status) => {
    if (!status) return null;
    const config = STATUS_CONFIG[status.toLowerCase()] || { label: status, cor: "#64748b" };
    return (
      <span 
        className="m-status-chip"
        style={{ 
          background: `${config.cor}20`,
          color: config.cor,
          borderColor: `${config.cor}40`
        }}
      >
        <span className="m-status-dot" style={{ background: config.cor }} />
        {config.label}
      </span>
    );
  };

  const defaultRenderCard = (item, index) => {
    const isExpanded = expandedCards[item.id];
    const isSwiped = swipedCard === item.id;
    const avatarUrl = item.avatarUrl || item.fotoUrl;
    
    return (
      <article 
        key={item.id} 
        className={`m-user-card ${isSwiped ? "swiped" : ""}`}
        style={{ animationDelay: `${index * 0.05}s` }}
        onTouchStart={(e) => handleTouchStart(e, item.id)}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => handleTouchEnd(item.id)}
      >
        <div className="m-user-card-topbar" />
        
        <div className="m-card-swipe-actions">
          <button 
            className="m-swipe-action m-swipe-edit"
            onClick={() => onEditar && onEditar(item.id)}
          >
            <IconEdit size="20px" />
          </button>
          {onExcluir && (
            <button 
              className="m-swipe-action m-swipe-delete"
              onClick={() => onExcluir(item.id, item.nome)}
            >
              <IconTrash size="20px" />
            </button>
          )}
        </div>
        
        <div className="m-card-content">
          <div className="m-card-header">
            <div className={`m-card-avatar${avatarUrl ? " has-image" : ""}`}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={item.nome ? `Foto de ${item.nome}` : "Foto do item"}
                  className="m-card-avatar-img"
                />
              ) : (
                getInitials(item.nome)
              )}
            </div>
            <div className="m-card-title">
              <h4 className="m-card-name">{item.nome || "Sem nome"}</h4>
              <div className="m-card-chips">
                {item.subtitulo && (
                  <span className="m-card-badge badge-tecnico">
                    {item.subtitulo}
                  </span>
                )}
                {item.status && renderStatusChip(item.status)}
              </div>
            </div>
            
            {item.detalhesExtras && (
              <button 
                className={`m-card-expand-btn ${isExpanded ? "expanded" : ""}`}
                onClick={() => toggleExpand(item.id)}
                aria-label={isExpanded ? "Recolher" : "Expandir"}
              >
                <IconChevronDown size="18px" />
              </button>
            )}
          </div>

          {item.detalhes && item.detalhes.length > 0 && (
            <div className="m-card-details">
              {item.detalhes.map((detalhe, idx) => (
                <div key={idx} className="m-card-detail-row">
                  <span className="m-card-detail-icon">{detalhe.icone}</span>
                  <span className="m-card-detail-label">{detalhe.label}</span>
                  <span className="m-card-detail-value">{detalhe.valor || "-"}</span>
                </div>
              ))}
            </div>
          )}

          {item.detalhesExtras && isExpanded && (
            <div className="m-card-extras">
              {item.detalhesExtras.map((detalhe, idx) => (
                <div key={idx} className="m-card-detail-row">
                  <span className="m-card-detail-icon">{detalhe.icone}</span>
                  <span className="m-card-detail-label">{detalhe.label}</span>
                  <span className="m-card-detail-value">{detalhe.valor || "-"}</span>
                </div>
              ))}
            </div>
          )}

          
          {onAcaoPrimaria && (
            <button
              type="button"
              className="m-card-primary-btn"
              onClick={() => onAcaoPrimaria(item)}
            >
              {acaoPrimariaIcon && <span className="m-card-primary-icon">{acaoPrimariaIcon}</span>}
              <span className="m-card-primary-text">
                {getAcaoPrimariaLabel ? getAcaoPrimariaLabel(item) : acaoPrimariaLabel}
              </span>
            </button>
          )}

          <div className="m-card-actions">
          {onEditar && (
            <button 
              className="m-card-action-btn m-action-edit" 
              title="Editar"
              onClick={() => onEditar && onEditar(item.id)}
            >
              <IconEdit size="18px" />
            </button>
          )}
{onExcluir && (
              <button 
                className="m-card-action-btn m-action-delete" 
                title="Excluir"
                onClick={() => onExcluir(item.id, item.nome)}
              >
                <IconTrash size="18px" />
              </button>
            )}
          </div>
        </div>
      </article>
    );
  };

  return (
    <>
      {showColorBar && (
        <div className="m-color-bar">
          <span className="m-color-dot" style={{ background: "#FFFFFF" }}></span>
          <span className="m-color-dot" style={{ background: "#808080" }}></span>
          <span className="m-color-dot" style={{ background: "#F5F5DC" }}></span>
          <span className="m-color-dot" style={{ background: "#1a1a1a" }}></span>
          <span className="m-color-dot" style={{ background: "#DC2626" }}></span>
        </div>
      )}

      {customActions ? (
        <div className="m-actions">
          {customActions}
        </div>
      ) : !hideDefaultActions && (onHome || onNovo) && (
        <div className="m-actions">
          {onHome && (
            <button 
              className="m-btn-pill m-btn-primary"
              onClick={onHome}
            >
              Home
            </button>
          )}
          {onNovo && (
            <button 
              className="m-btn-pill m-btn-secondary"
              onClick={onNovo}
            >
              {novoLabel}
            </button>
          )}
        </div>
      )}

      <div className="m-search-wrapper">
        <span className="m-search-icon">
          <IconSearch size="18px" />
        </span>
        <input
          type="text"
          placeholder={searchPlaceholder}
          className="m-search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button 
            className="m-search-clear" 
            onClick={() => setSearchTerm("")}
          >
            <IconClose size="16px" />
          </button>
        )}
      </div>

      {erro && (
        <div className="m-error-banner">
          {erro}
        </div>
      )}

      {carregando ? (
        <div className="m-loading-state">
          <span className="m-loading-spinner">
            <IconLoading size="40px" />
          </span>
          <p>{carregandoMessage}</p>
        </div>
      ) : (
        <>
          <div className="m-results-count">
            {filteredItems.length} {filteredItems.length === 1 ? "resultado" : "resultados"}
          </div>

          <section className="m-user-list">
            {filteredItems.map((item, index) => 
              renderCard ? renderCard(item, index, { onEditar, onExcluir, getInitials, renderStatusChip }) : defaultRenderCard(item, index)
            )}

            {filteredItems.length === 0 && (
              <div className="m-empty-state">
                <span className="m-empty-icon">
                  <IconSearch size="48px" color="var(--cor-texto-apagado)" />
                </span>
                <p>{emptyMessage}</p>
                <span className="m-empty-hint">Tente buscar por outro termo</span>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}

export default CardListMobile;
